<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة المدد</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة مدد المستخدمين والدورات</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="openAddModal"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors">
          إضافة مدة جديدة
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في المدد..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="relative">
        <select
          v-model="filterActive"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option :value="null">جميع المدد</option>
          <option :value="true">مدد نشطة فقط</option>
          <option :value="false">مدد غير نشطة</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي المدد: {{ durations.length }}
      </div>
    </div>

    <!-- Durations Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">قائمة المدد</h1>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredDurations.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="mt-2 text-gray-600">
          {{ searchTerm || filterActive !== null ? 'لا توجد مدد تطابق البحث' : 'لا توجد مدد مسجلة' }}
        </p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">وصف المدة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">قيمة المدة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الحالة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعديل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حذف</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(duration, index) in filteredDurations" :key="duration.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{{ duration.durationDesc }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ duration.durationValue || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span 
                      :class="[
                        'px-2 py-1 rounded-full text-xs font-medium',
                        duration.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      ]">
                      {{ duration.isActive ? 'نشط' : 'غير نشط' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openEditModal(duration)">تعديل</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button 
                      class="text-red-600 hover:text-red-800" 
                      @click="handleDelete(duration)">
                      حذف
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          عرض {{ pagination.from }} إلى {{ pagination.to }} من {{ pagination.total }} نتائج
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            السابق
          </button>
          <button
            @click="changePage(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            التالي
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 500px;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          {{ editMode ? 'تعديل المدة' : 'إضافة مدة جديدة' }}
        </h3>
        
        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="duration_desc" class="block text-sm font-medium text-gray-700 mb-2">وصف المدة *</label>
              <input
                v-model="formData.duration_desc"
                type="text"
                id="duration_desc"
                required
                placeholder="أدخل وصف المدة"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="duration_value" class="block text-sm font-medium text-gray-700 mb-2">قيمة المدة</label>
              <input
                v-model="formData.duration_value"
                type="number"
                id="duration_value"
                placeholder="أدخل قيمة المدة (اختياري)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label class="flex items-center">
                <input
                  v-model="formData.is_active"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span class="mr-2 text-sm text-gray-700">مفعل</span>
              </label>
            </div>
          </div>

          <div class="mt-6 flex gap-3 justify-end">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              إلغاء
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-4 py-2 bg-customHafadhColor text-white rounded-md text-sm font-medium hover:bg-customHafadhColor/90 disabled:opacity-50">
              {{ saving ? 'جاري الحفظ...' : (editMode ? 'تحديث' : 'إضافة') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'DurationsManagement',
  data() {
    return {
      durations: [],
      loading: false,
      saving: false,
      searchTerm: '',
      filterActive: null,
      showModal: false,
      editMode: false,
      formData: {
        id: null,
        duration_desc: '',
        duration_value: null,
        is_active: true,
      },
      alert: {
        show: false,
        type: 'success',
        message: '',
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        from: 0,
        to: 0,
      },
    }
  },
  computed: {
    filteredDurations() {
      let filtered = this.durations

      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase()
        filtered = filtered.filter(duration => 
          duration.durationDesc?.toLowerCase().includes(search) ||
          String(duration.durationValue || '').includes(search)
        )
      }

      if (this.filterActive !== null) {
        filtered = filtered.filter(duration => duration.isActive === this.filterActive)
      }

      return filtered
    }
  },
  watch: {
    searchTerm() {
      this.pagination.currentPage = 1
    },
    filterActive() {
      this.pagination.currentPage = 1
      this.loadDurations()
    },
  },
  mounted() {
    this.loadDurations()
  },
  methods: {
    async loadDurations() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          size: 20,
        }
        if (this.filterActive !== null) {
          params.is_active = this.filterActive
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }

        const { data } = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.durations_list,
          {
            params,
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        // Handle nested response structure: data.result.data.data and data.result.data.pagination
        if (data.result && data.result.data) {
          if (Array.isArray(data.result.data)) {
            // Direct array response
            this.durations = data.result.data
          } else if (data.result.data.data && Array.isArray(data.result.data.data)) {
            // Nested structure: result.data.data
            this.durations = data.result.data.data
            if (data.result.data.pagination) {
              this.pagination = {
                currentPage: data.result.data.pagination.current_page || 1,
                totalPages: data.result.data.pagination.total_pages || 1,
                total: data.result.data.pagination.total || 0,
                from: data.result.data.pagination.from || 0,
                to: data.result.data.pagination.to || 0,
              }
            }
          } else {
            this.durations = []
          }
        } else {
          this.durations = []
        }
      } catch (error) {
        console.error('Failed to load durations', error)
        this.showAlert('danger', 'فشل في تحميل المدد')
        this.durations = []
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editMode = false
      this.formData = {
        id: null,
        duration_desc: '',
        duration_value: null,
        is_active: true,
      }
      this.showModal = true
    },
    openEditModal(duration) {
      this.editMode = true
      this.formData = {
        id: duration.id,
        duration_desc: duration.durationDesc,
        duration_value: duration.durationValue,
        is_active: duration.isActive,
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editMode = false
      this.formData = {
        id: null,
        duration_desc: '',
        duration_value: null,
        is_active: true,
      }
    },
    async handleSave() {
      this.saving = true
      try {
        const payload = {
          duration_desc: this.formData.duration_desc,
          duration_value: this.formData.duration_value ? parseInt(this.formData.duration_value) : null,
          is_active: this.formData.is_active,
        }

        if (this.editMode) {
          await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.durations_update + this.formData.id,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم تحديث المدة بنجاح')
        } else {
          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.durations_create,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم إضافة المدة بنجاح')
        }

        this.closeModal()
        this.loadDurations()
      } catch (error) {
        console.error('Failed to save duration', error)
        const message = error.response?.data?.result?.message_ar || error.response?.data?.message_ar || 'فشل في حفظ المدة'
        this.showAlert('danger', message)
      } finally {
        this.saving = false
      }
    },
    async handleDelete(duration) {
      if (!confirm(`هل أنت متأكد من حذف المدة "${duration.durationDesc}"؟`)) {
        return
      }

      try {
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.durations_delete + duration.id,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        )
        this.showAlert('success', 'تم حذف المدة بنجاح')
        this.loadDurations()
      } catch (error) {
        console.error('Failed to delete duration', error)
        const message = error.response?.data?.result?.message_ar || error.response?.data?.message_ar || 'فشل في حذف المدة'
        this.showAlert('danger', message)
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page
        this.loadDurations()
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    },
  },
})
</script>

<style scoped>
.popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.popup-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.enabled-button {
  color: #3b82f6;
  font-weight: 500;
  cursor: pointer;
}

.enabled-button:hover {
  color: #2563eb;
  text-decoration: underline;
}
</style>

